import React, { useMemo } from 'react';
import { CobuText } from '@cobu/ui';

interface AutoLinkTextProps {
  text: string;
  variant?: 'body' | 'caption' | 'heading';
  fontSize?: any;
  fontWeight?: any;
  color?: any;
  lineHeight?: number;
  appendEllipsis?: boolean;
}

// Detect http(s) URLs, www.* domains, and plain emails
const LINK_REGEX = /((?:https?:\/\/|www\.)[^\s]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

function normalizeHref(raw: string): string {
  if (!raw) return '';
  const value = raw.trim();
  const isEmail = value.includes('@') && !value.includes(' ');
  if (isEmail) return `mailto:${value}`;
  if (/^https?:\/\//i.test(value)) return value;
  if (/^www\./i.test(value)) return `https://${value}`;
  return value;
}

function trimTrailingPunctuation(value: string): { main: string; trailing: string } {
  const match = value.match(/[\)\]\.,;!?]+$/);
  if (!match) return { main: value, trailing: '' };
  const trailing = match[0];
  return { main: value.slice(0, -trailing.length), trailing };
}

const AutoLinkText: React.FC<AutoLinkTextProps> = ({
  text,
  variant = 'body',
  fontSize = '$3',
  fontWeight = 'normal',
  color = '$color',
  lineHeight = 20,
  appendEllipsis = false,
}) => {
  const parts = useMemo(() => {
    if (!text) return [{ type: 'text' as const, value: '' }];
    const results: Array<{ type: 'text' | 'link'; value: string; href?: string }> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    const regex = new RegExp(LINK_REGEX);
    regex.lastIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      const matchText = match[0];
      const start = match.index;
      const end = start + matchText.length;

      if (start > lastIndex) {
        results.push({ type: 'text', value: text.slice(lastIndex, start) });
      }

      const { main, trailing } = trimTrailingPunctuation(matchText);
      if (main.length > 0) {
        const href = normalizeHref(main);
        results.push({ type: 'link', value: main.toLowerCase(), href });
      }
      if (trailing) {
        results.push({ type: 'text', value: trailing });
      }

      lastIndex = end;
    }

    if (lastIndex < text.length) {
      results.push({ type: 'text', value: text.slice(lastIndex) });
    }
    return results;
  }, [text]);

  return (
    <CobuText
      variant={variant}
      fontSize={fontSize}
      fontWeight={fontWeight}
      color={color}
      lh={lineHeight}
      ta="left"
      selectable
    >
      {parts.map((p, idx) =>
        p.type === 'link' ? (
          <CobuText
            key={`link-${idx}`}
            variant={variant}
            fontSize={fontSize}
            fontWeight={fontWeight}
            color="$textLinkColor"
            role="link"
            selectable
            asChild
          >
            <a href={p.href} target="_blank" rel="noopener noreferrer">{p.value}</a>
          </CobuText>
        ) : (
          <CobuText
            key={`text-${idx}`}
            variant={variant}
            fontSize={fontSize}
            fontWeight={fontWeight}
            color={color}
            selectable
          >
            {p.value}
          </CobuText>
        )
      )}
      {appendEllipsis ? '...' : ''}
    </CobuText>
  );
};

export default AutoLinkText;


